import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Globe, 
  Smartphone, 
  Search, 
  BarChart3,
  Workflow,
  Users,
  ClipboardList,
  Settings,
  Monitor,
  Cpu,
  Zap,
  Database,
  Cog,
  TrendingUp,
  Target,
  Layers,
  ArrowRight,
  CheckCircle2,
  Code,
  Sparkles
} from "lucide-react";
import SEOHead from "@/components/SEOHead";

const services = [
  {
    id: "web-development",
    icon: Globe,
    title: "Website Development",
    tagline: "Build Your Digital Presence",
    description: "Professional websites and landing pages designed for performance and lead generation. We create stunning, fast, and conversion-focused web experiences.",
    image: "https://images.unsplash.com/photo-1735713212083-82eafc42bf64?w=1200&q=80",
    color: "from-blue-500 to-cyan-500",
    bgGlow: "bg-blue-500/20",
    features: [
      { icon: Globe, text: "Landing pages" },
      { icon: Monitor, text: "Business websites" },
      { icon: Target, text: "Conversion-focused design" },
      { icon: Smartphone, text: "Mobile optimization" },
      { icon: Search, text: "SEO foundation" },
      { icon: BarChart3, text: "Analytics integrations" },
    ],
  },
  {
    id: "workflow-apps",
    icon: Workflow,
    title: "Custom Workflow Apps",
    tagline: "Streamline Your Operations",
    description: "We build CRMs, internal platforms, dashboards, and workflow automation tools tailored for businesses. Transform how your team works.",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&q=80",
    color: "from-violet-500 to-purple-500",
    bgGlow: "bg-violet-500/20",
    features: [
      { icon: Users, text: "Custom CRMs" },
      { icon: BarChart3, text: "Internal dashboards" },
      { icon: ClipboardList, text: "Project management tools" },
      { icon: Settings, text: "Business automation systems" },
      { icon: Monitor, text: "Monitoring platforms" },
    ],
  },
  {
    id: "ai-solutions",
    icon: Cpu,
    title: "AI Solutions",
    tagline: "Intelligence at Scale",
    description: "AI-powered automation, intelligent workflows, and modern digital systems. Harness the power of artificial intelligence for your business.",
    image: "https://images.unsplash.com/photo-1764336312138-14a5368a6cd3?w=1200&q=80",
    color: "from-blue-600 to-violet-600",
    bgGlow: "bg-indigo-500/20",
    features: [
      { icon: Zap, text: "Workflow automation" },
      { icon: Cpu, text: "AI integrations" },
      { icon: Cog, text: "Custom AI-powered tools" },
      { icon: Database, text: "Data processing solutions" },
    ],
  },
  {
    id: "marketing-tech",
    icon: TrendingUp,
    title: "Marketing Technology",
    tagline: "Data-Driven Growth",
    description: "Analytics, tracking systems, conversion optimization and automation tools. Make informed decisions with powerful marketing infrastructure.",
    image: "https://images.unsplash.com/photo-1752253604157-65fb42c30816?w=1200&q=80",
    color: "from-purple-500 to-pink-500",
    bgGlow: "bg-purple-500/20",
    features: [
      { icon: BarChart3, text: "Analytics setup" },
      { icon: Target, text: "Conversion tracking" },
      { icon: Layers, text: "Automation systems" },
      { icon: Settings, text: "Marketing infrastructure" },
    ],
  },
];

export const ServicesPage = () => {
  const [hoveredService, setHoveredService] = useState(null);

  return (
    <>
      <SEOHead 
        title="Our Services | Global Data XM - Web, AI & Marketing Solutions"
        description="Explore our services: Website Development, Custom Workflow Apps, AI Solutions, and Marketing Technology designed to help businesses scale."
      />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-slate-900 relative overflow-hidden" data-testid="services-hero">
        {/* Background elements */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900" />
        <div className="absolute top-20 left-10 w-96 h-96 bg-blue-600/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-violet-600/10 rounded-full blur-[120px]" />
        
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/10 rounded-full px-4 py-2 mb-6"
            >
              <Sparkles size={16} className="text-violet-400" />
              <span className="text-sm font-medium text-slate-300">What We Offer</span>
            </motion.div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Our{" "}
              <span className="bg-gradient-to-r from-blue-400 via-violet-400 to-purple-400 bg-clip-text text-transparent">
                Services
              </span>
            </h1>
            <p className="text-lg text-slate-400 leading-relaxed">
              Comprehensive digital solutions to help your business thrive in the modern landscape.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Showcase */}
      <section className="bg-slate-900" data-testid="services-list">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
              className="py-20 border-b border-white/5 last:border-none"
              data-testid={`service-${service.id}`}
            >
              <div className={`grid lg:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? "lg:flex-row-reverse" : ""
              }`}>
                {/* Content */}
                <div className={index % 2 === 1 ? "lg:order-2" : ""}>
                  <motion.div
                    initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 }}
                  >
                    {/* Icon & Tag */}
                    <div className="flex items-center gap-4 mb-6">
                      <div className={`w-16 h-16 bg-gradient-to-r ${service.color} rounded-2xl flex items-center justify-center shadow-lg`}>
                        <service.icon size={32} className="text-white" />
                      </div>
                      <div>
                        <span className="text-sm text-slate-500 uppercase tracking-wider">{service.tagline}</span>
                      </div>
                    </div>

                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
                      {service.title}
                    </h2>
                    <p className="text-slate-400 text-lg mb-8 leading-relaxed">
                      {service.description}
                    </p>

                    {/* Features Grid */}
                    <div className="grid grid-cols-2 gap-4 mb-8">
                      {service.features.map((feature, fIndex) => (
                        <motion.div
                          key={fIndex}
                          initial={{ opacity: 0, y: 10 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: 0.3 + fIndex * 0.05 }}
                          className="flex items-center gap-3 p-3 bg-white/5 rounded-xl border border-white/5 hover:border-white/10 transition-colors"
                        >
                          <CheckCircle2 size={18} className="text-green-400 flex-shrink-0" />
                          <span className="text-sm text-slate-300">{feature.text}</span>
                        </motion.div>
                      ))}
                    </div>

                    <Link
                      to="/contact"
                      className={`inline-flex items-center gap-2 bg-gradient-to-r ${service.color} text-white px-6 py-3 rounded-full font-medium hover:shadow-lg transition-all hover:scale-105`}
                    >
                      Get Started
                      <ArrowRight size={18} />
                    </Link>
                  </motion.div>
                </div>

                {/* Visual */}
                <div className={index % 2 === 1 ? "lg:order-1" : ""}>
                  <motion.div
                    initial={{ opacity: 0, x: index % 2 === 0 ? 30 : -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 }}
                    className="relative"
                    onMouseEnter={() => setHoveredService(service.id)}
                    onMouseLeave={() => setHoveredService(null)}
                  >
                    {/* Glow effect */}
                    <div className={`absolute inset-0 ${service.bgGlow} blur-[60px] opacity-50 transition-opacity ${
                      hoveredService === service.id ? "opacity-80" : ""
                    }`} />
                    
                    {/* Card */}
                    <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-3xl p-2 border border-white/10 overflow-hidden group">
                      {/* Animated border */}
                      <div className={`absolute inset-0 bg-gradient-to-r ${service.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl`} />
                      
                      <div className="relative rounded-2xl overflow-hidden">
                        <img
                          src={service.image}
                          alt={service.title}
                          loading="lazy"
                          decoding="async"
                          className="w-full aspect-[4/3] object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        
                        {/* Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/20 to-transparent" />
                        
                        {/* Floating elements */}
                        <motion.div
                          animate={{ y: [-5, 5, -5] }}
                          transition={{ duration: 3, repeat: Infinity }}
                          className="absolute top-4 right-4 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/20"
                        >
                          <Code size={20} className="text-white" />
                        </motion.div>
                        
                        {/* Bottom info */}
                        <div className="absolute bottom-0 left-0 right-0 p-6">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 bg-gradient-to-r ${service.color} rounded-lg flex items-center justify-center`}>
                              <service.icon size={20} className="text-white" />
                            </div>
                            <div>
                              <p className="text-white font-semibold">{service.title}</p>
                              <p className="text-slate-400 text-sm">{service.tagline}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-r from-blue-600 via-violet-600 to-purple-600 relative overflow-hidden" data-testid="services-cta">
        {/* Animated background */}
        <div className="absolute inset-0">
          <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 8, repeat: Infinity }}
            className="absolute top-0 left-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl"
          />
          <motion.div
            animate={{ scale: [1.2, 1, 1.2], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 10, repeat: Infinity }}
            className="absolute bottom-0 right-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl"
          />
        </div>
        
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              Need a Custom Solution?
            </h2>
            <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto">
              Let's discuss your specific requirements and create something tailored for your business.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-white text-blue-600 px-8 py-4 rounded-full font-medium hover:shadow-xl transition-all hover:scale-105"
              data-testid="services-cta-btn"
            >
              Get in Touch
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default ServicesPage;
